import { GoogleSignUpButton } from '@/components/auth/GoogleSignUpButton';
import { SignupCodeStep } from '@/pages/signup/_components/SignupCodeStep';
import type { SignupDetails } from '@/pages/signup/_components/signup-details';
import { SignupForm } from '@/pages/signup/_components/SignupForm';
import { Link, Stack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthError } from './auth-error';
import { OrSeparator } from './OrSeparator';

interface SignupPanelProps {
  oauthError: AuthError | null;
  /** The admin invitation the account is created from; none for a self sign-up. */
  invitationToken?: string;
  onCreated: () => void;
  /** Told when the panel moves to, or back from, the code sent to the address. */
  onVerifyingChange?: (verifying: boolean) => void;
}

/** Google, or email and password, to create a brand account. */
export const SignupPanel = ({
  oauthError,
  invitationToken,
  onCreated,
  onVerifyingChange,
}: SignupPanelProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<AuthError | null>(oauthError);
  // What the form collected while the brand proves its address; kept to come
  // back to the filled form.
  const [details, setDetails] = useState<SignupDetails | null>(null);
  const [verifying, setVerifying] = useState(false);

  const showCode = (next: SignupDetails) => {
    setDetails(next);
    setError(null);
    setVerifying(true);
    onVerifyingChange?.(true);
  };

  const backToForm = (error?: AuthError) => {
    setError(error ?? null);
    setVerifying(false);
    onVerifyingChange?.(false);
  };

  // Sync OAuth error from parent when mode switches
  useEffect(() => {
    if (oauthError) setError(oauthError);
  }, [oauthError]);

  if (verifying && details) {
    return <SignupCodeStep details={details} onChangeEmail={backToForm} onCreated={onCreated} />;
  }

  return (
    <Stack gap={4} width="full">
      <GoogleSignUpButton invitationToken={invitationToken} />
      <OrSeparator label={t('signup.or')} />

      <SignupForm
        error={error}
        onError={setError}
        onSuccess={onCreated}
        onCodeSent={showCode}
        invitationToken={invitationToken}
        initialValues={details ?? undefined}
      />

      <Text fontSize="11.5px" color="gray.600" lineHeight="1.5" textAlign="center" mt={1}>
        {t('signup.disclaimer')}{' '}
        <Link
          href="https://www.fluenceur.com/terms-and-conditions"
          color="primary.900"
          textDecor="underline"
          _hover={{ color: 'primary.800' }}
        >
          {t('signup.termsLink')}
        </Link>{' '}
        {t('signup.and')}{' '}
        <Link
          href="https://www.fluenceur.com/privacy-policy"
          color="primary.900"
          textDecor="underline"
          _hover={{ color: 'primary.800' }}
        >
          {t('signup.privacyLink')}
        </Link>
        .
      </Text>
    </Stack>
  );
};
